import "./InputSearch.scss"

import React, { useState, useMemo, FunctionComponent, useEffect, useCallback, useRef, KeyboardEvent } from "react"

import { Flex, FlexItem } from "components/Flex"

import iconClearIndicator from "assets/img/icon-clear-indicator.svg"
import Input from "components/Input/Input"
import { GroupedControl } from "config/constants"
import { debounce } from "lodash"
import HelpButton, { IHelpButtonProps } from "components/HelpButton/HelpButton"

interface IInputSearchProps {
    className?: string
    placeholder?: string
    readonly?: boolean
    name?: string
    value?: string
    width?: string
    maxLength?: number
    isInvalid?: boolean
    autoFocus?: boolean
    "data-testid"?: string
    helpTooltip?: IHelpButtonProps["tooltipProps"]
    grouped?: GroupedControl,
    debounceMs?: number
    onBlur?(): void
    onFocus?(value: string): void
    onChange?(value: string): void
    onPressEnter?(value: string): void
    onReset?(): void
    disabled?: boolean
}

const InputSearch: FunctionComponent<React.PropsWithChildren<IInputSearchProps>> = ({
    className = "",
    placeholder,
    readonly = false,
    name,
    value,
    width,
    maxLength,
    helpTooltip,
    debounceMs = 500,
    onBlur,
    onFocus,
    onChange,
    onPressEnter,
    onReset,
    disabled,
    ...others
}) => {
    const [inpValue, setInpValue] = useState(value ||"")
    const lastEmittedRef = useRef<string | undefined>(undefined)
    const shouldShowReset = !!inpValue && !!onReset
    const shouldShowTooltip = !!helpTooltip

    useEffect(() => {
        // The debounced onChange lets `value` lag behind what is already typed - adopting the
        // parent echo of an earlier edit would rewind the input and swallow characters (EMTMP-1304).
        if ((value || "") === lastEmittedRef.current) return
        setInpValue(value || "")
    }, [value])

    // eslint-disable-next-line react-hooks/refs -- the ref is written when the debounce fires, not during render
    const deboucedOnChange = useMemo(() => debounce((newValue: string) => {
        lastEmittedRef.current = newValue
        onChange && onChange(newValue)
    }, debounceMs), [debounceMs, onChange])

    const trigOnChange = useCallback((newValue: string) => {
        deboucedOnChange(newValue)
    }, [deboucedOnChange])

    const handleOnChange = (e) => {
        setInpValue(e.currentTarget.value)
        trigOnChange(e.currentTarget.value)
    }

    const handleResetClick = () => {
        // a still pending emit would restore the text we just cleared
        deboucedOnChange.cancel()
        setInpValue("")
        if (typeof onReset === "function") {
            onReset()
        }
    }

    const handleKeyPress = (e: KeyboardEvent) => {
        if (e.key !== "Enter") return
        e.stopPropagation()
        e.preventDefault()
        onPressEnter && onPressEnter(inpValue)
    }

    const getInputPaddingRight = useCallback(() => {
        const padding = (shouldShowReset ? 40 : 0) + (shouldShowTooltip ? 40 : 0)
        if (padding === 0) return
        return `${padding}px`
    }, [shouldShowReset, shouldShowTooltip])

    return (
        <div className={`input-search ${className}`} style={{width}}>
            <Input
                disabled={disabled}
                type="text"
                width="100%"
                name={name}
                placeholder={placeholder}
                readOnly={readonly}
                value={inpValue}
                onChange={handleOnChange}
                onBlur={onBlur}
                onFocus={() => onFocus && onFocus(inpValue)}
                onKeyPress={handleKeyPress}
                style={{ paddingRight: getInputPaddingRight() }}
                {...others}/>
            <Flex className="input-search__indicators"
                alignItems="center">
                {shouldShowReset &&
                    <FlexItem>
                        <button
                            type="button"
                            className="input-search__reset"
                            aria-label="Reset"
                            data-testid="input-search-reset"
                            onClick={handleResetClick}>
                            <img src={iconClearIndicator} alt="" />
                        </button>
                    </FlexItem>
                }
                {shouldShowTooltip &&
                    <FlexItem>
                        <div className="input-search__tooltip">
                            <HelpButton tooltipProps={helpTooltip} />
                        </div>
                    </FlexItem>
                }
            </Flex>
        </div>
    )
}

export default InputSearch
